import { useUserMe } from "~/components/user/composables/useUser";
import type { UpdateTalentBody } from "@asap/shared/schemas/talent/talent";
import type { TalentQualificationStatus } from "@asap/shared/utils/supabase.enum";
import type { Talent } from "@asap/shared/utils/supabase.types";
import { useApi } from "~/composables/useApi";
import { useTalentEvents } from "~/components/talent/composables/useTalentEvents";
import { useTalentQuery } from "~/components/talent/composables/useTalentQuery";

export const useTalentUpdate = function () {
  const { selectSingle } = useTalentQuery();
  const { invoke } = useApi();

  // The API owns the write so criterion changes emit their domain event.
  //
  // Callers pass whole form payloads parsed by schemas that include `id` / `updated_at` for their
  // own validation. Both are server-owned and the endpoint rejects unknown keys, so they are
  // dropped here rather than at every call site.
  const update = async function (id: string, talent: UpdateTalentBody & { id?: string; updated_at?: string }) {
    const { id: _ignoredId, updated_at: _ignoredUpdatedAt, ...updatableFields } = talent;

    const response = await invoke({
      path: "talent/:talentId",
      method: "PATCH",
      params: { talentId: id },
      body: updatableFields,
    });

    if (!response.success) throw new Error(response.error.message);
  };

  const updateReferencing = async function (id: string, payload: { active: boolean; comment?: string }) {
    const { authUser } = useUserMe();
    if (!authUser.value) throw new Error("User not found");

    const values = {
      active: payload.active,
      dereferenced_at: new Date().toISOString(),
      dereferenced_by: authUser.value.id,
      dereference_reason: payload.comment,
    };

    const response = await invoke({
      path: "talent/:talentId/referencing",
      method: "PATCH",
      params: { talentId: id },
      body: values,
    });

    if (!response.success) throw new Error(response.error.message);
    return { data: response.data, values: { ...values, dereference_reason: values.dereference_reason ?? "" } };
  };

  const updateTalentQualificationStatus = async function (
    talentPick: Pick<Talent, "id" | "qualification_status">,
    status: TalentQualificationStatus
  ) {
    await update(talentPick.id, { qualification_status: status });

    const { qualificationStatusCaptureEvents } = useTalentEvents();
    qualificationStatusCaptureEvents(talentPick, status);

    // Callers bind to the wide talent shape, which the endpoint does not return — re-read it.
    const { data } = await selectSingle(talentPick.id);

    return data;
  };

  const updateBlockAdvancePayment = async function (
    talentId: string,
    payload: { blocked: boolean; reason?: string | null }
  ) {
    const response = await invoke({
      path: "talent/:talentId/block-advance-payment",
      method: "PATCH",
      params: { talentId },
      body: { blocked: payload.blocked, reason: payload.reason ?? null },
    });

    if (!response.success) throw new Error(response.error.message);
  };

  return {
    update,
    updateReferencing,
    updateTalentQualificationStatus,
    updateBlockAdvancePayment,
  };
};
